<template>
    <div>
        <h2>Compute Your Toll Fee</h2>

        <p v-if="loadError" class="calc-error">
            Could not load expressway data. Please refresh the page.
        </p>
        <p v-else-if="!expressways.length" class="calc-loading">Loading toll points…</p>

        <template v-else>
            <PointAutocomplete
                v-model="entryPoint"
                label="Entry point"
                input-id="tc-entry"
                placeholder="e.g. Balintawak, Alabang, Tarlac…"
                :points="allPoints"
                :restrict-expressway-id="exitPoint?.expresswayId ?? null"
                :exclude-id="exitPoint?.id ?? null"
                @switch-road="switchRoad('entry', $event)"
            />

            <button type="button" class="swap-btn" @click="swap" :disabled="!entryPoint && !exitPoint">
                <svg class="ico" aria-hidden="true"><use href="#i-arrows-up-down"></use></svg> Swap direction
            </button>

            <PointAutocomplete
                v-model="exitPoint"
                label="Exit point"
                input-id="tc-exit"
                placeholder="e.g. Dau, Calamba, Batangas City…"
                :points="allPoints"
                :restrict-expressway-id="entryPoint?.expresswayId ?? null"
                :exclude-id="entryPoint?.id ?? null"
                @switch-road="switchRoad('exit', $event)"
            />

            <div class="field">
                <label>Vehicle class</label>
                <div class="class-cards">
                    <button
                        v-for="c in classes"
                        :key="c.value"
                        type="button"
                        :class="{ selected: vehicleClass === c.value }"
                        @click="pickClass(c.value)"
                    >
                        <svg class="ico" aria-hidden="true"><use :href="'#i-' + String(c.icon).slice(3)"></use></svg>
                        <span class="class-name">Class {{ c.value }}</span>
                        <span class="class-desc">{{ c.desc }}</span>
                    </button>
                </div>
            </div>

            <div class="calc-actions">
                <button type="button" class="btn" @click="calculate" :disabled="!canCalculate || calculating">
                    {{ calculating ? 'Computing…' : 'Calculate Toll Fee' }}
                </button>
                <button type="button" class="btn btn-ghost" @click="clearAll">Clear All</button>
            </div>

            <p v-if="error" class="calc-error">{{ error }}</p>

            <div v-if="result" class="result-card">
                <div class="result-top">
                    <div class="result-info">
                        <h3 class="result-route">
                            <svg class="ico" aria-hidden="true"><use href="#i-map-location-dot"></use></svg>
                            {{ result.entry }} <svg class="ico result-arrow" aria-hidden="true"><use href="#i-arrow-right-long"></use></svg> {{ result.exit }}
                        </h3>
                        <div class="result-chips">
                            <span class="result-chip"><svg class="ico" aria-hidden="true"><use href="#i-road"></use></svg> {{ expresswayCode }}</span>
                            <span v-if="interchangesPassed !== null" class="result-chip">
                                <svg class="ico" aria-hidden="true"><use href="#i-right-left"></use></svg>
                                {{ interchangesPassed }} interchange{{ interchangesPassed === 1 ? '' : 's' }}
                            </span>
                            <span class="result-chip"><svg class="ico" aria-hidden="true"><use href="#i-credit-card"></use></svg> {{ rfidNetwork }} RFID</span>
                        </div>
                        <div class="trip-toggle">
                            <button type="button" :class="{ selected: !roundTrip }" @click="pickTrip(false)">One way</button>
                            <button type="button" :class="{ selected: roundTrip }" @click="pickTrip(true)">Round trip</button>
                        </div>
                    </div>
                    <div class="result-fee">
                        <span class="fee-label">Total Toll Fee</span>
                        <span class="fee-amount">₱{{ formatRate(displayedRate) }}</span>
                        <span class="fee-sub">Class {{ vehicleClass }} · {{ roundTrip ? 'round trip' : 'one way' }}</span>
                        <span v-if="recommendedBalance" class="fee-balance">
                            <svg class="ico" aria-hidden="true"><use href="#i-credit-card"></use></svg>
                            Recommended RFID balance: <strong>₱{{ formatRate(recommendedBalance) }}</strong>
                        </span>
                    </div>
                </div>
            </div>

            <button v-if="result" type="button" class="details-trigger" @click="openDetails()">
                <svg class="ico" aria-hidden="true"><use href="#i-list-ul"></use></svg> View Route Details
                <svg class="ico details-chevron" aria-hidden="true"><use href="#i-chevron-right"></use></svg>
            </button>

            <RouteDetailsModal
                v-if="showDetails && resultEntry && resultExit"
                :entry-id="resultEntry.id"
                :exit-id="resultExit.id"
                :entry-name="resultEntry.name"
                :exit-name="resultExit.name"
                :vehicle-class="vehicleClass"
                :round-trip="roundTrip"
                @close="showDetails = false"
            />

            <div v-if="result" class="result-actions result-actions-bar">
                <button type="button" @click="copySummary">
                    <svg class="ico" aria-hidden="true"><use href="#i-copy"></use></svg> {{ copied ? '✓ Copied!' : 'Copy Summary' }}
                </button>
                <button type="button" @click="shareResult">
                    <svg class="ico" aria-hidden="true"><use href="#i-share-nodes"></use></svg> {{ shared ? '✓ Shared!' : 'Share' }}
                </button>
                <a :href="fuelUrl" @click="resultAction('add_fuel')"><svg class="ico" aria-hidden="true"><use href="#i-gas-pump"></use></svg> Add fuel cost</a>
                <a v-if="directionsUrl" :href="directionsUrl" target="_blank" rel="noopener" @click="resultAction('directions')">
                    <svg class="ico" aria-hidden="true"><use href="#i-diamond-turn-right"></use></svg> Directions to {{ resultExit?.name }}
                </a>
            </div>
        </template>
    </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import PointAutocomplete from './PointAutocomplete.vue';
import RouteDetailsModal from './RouteDetailsModal.vue';
import { track, trackOnce } from '../analytics';

const expressways = ref([]);
const loadError = ref(false);
const entryPoint = ref(null);
const exitPoint = ref(null);
const vehicleClass = ref(1);
const result = ref(null);
const error = ref('');
const calculating = ref(false);
const roundTrip = ref(false);
const shared = ref(false);
const copied = ref(false);
// Points captured at calculation time, so chips stay stable while the user
// edits the inputs for a new query.
const resultEntry = ref(null);
const resultExit = ref(null);
const showDetails = ref(false);

// Fires on the first point chosen, once per page. Without it there is no
// denominator: calculations alone cannot say how many people started and
// gave up partway.
watch([entryPoint, exitPoint], ([entry, exit]) => {
    if (entry || exit) trackOnce('start:toll', 'calc_start', { tool: 'toll' });
});

const classes = [
    { value: 1, icon: 'fa-car-side', desc: 'Cars, SUVs & vans' },
    { value: 2, icon: 'fa-bus', desc: 'Buses & light trucks' },
    { value: 3, icon: 'fa-truck-moving', desc: 'Heavy trucks & trailers' },
];

const allPoints = computed(() => expressways.value.flatMap((e) =>
    e.points.map((p) => ({
        id: p.id,
        name: p.name,
        expresswayId: e.id,
        code: e.code,
        expresswayName: e.name,
    }))
));

const canCalculate = computed(() =>
    entryPoint.value && exitPoint.value && entryPoint.value.id !== exitPoint.value.id
);

const resultExpressway = computed(() =>
    expressways.value.find((e) => e.id === resultEntry.value?.expresswayId) ?? null
);

const rfidNetwork = computed(() => resultExpressway.value?.rfid || 'RFID');
const expresswayCode = computed(() => resultExpressway.value?.code || '');

const interchangesPassed = computed(() => {
    const e = resultExpressway.value;
    if (!e || !resultEntry.value || !resultExit.value) return null;
    const idxA = e.points.findIndex((p) => p.id === resultEntry.value.id);
    const idxB = e.points.findIndex((p) => p.id === resultExit.value.id);
    if (idxA < 0 || idxB < 0) return null;
    return Math.max(0, Math.abs(idxA - idxB) - 1);
});

const recommendedBalance = computed(() => {
    if (displayedRate.value === null) return null;
    // Selected trip's fee + ~10% margin, rounded up to the nearest ₱10.
    return Math.ceil((displayedRate.value * 1.1) / 10) * 10;
});

// Google Maps directions from the user's current location (origin omitted →
// Maps uses "Your location") to the exit toll point.
const directionsUrl = computed(() => {
    if (!resultExit.value || !resultExpressway.value) return null;
    const destination = `${resultExit.value.name} Exit, ${resultExpressway.value.name}, Philippines`;
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}&travelmode=driving`;
});

const displayedRate = computed(() => {
    if (!result.value) return null;
    return tripMultiple(result.value.rates[String(vehicleClass.value)]);
});

const suggestedLoad = computed(() => {
    if (!result.value) return null;
    const oneWay = result.value.rates[String(vehicleClass.value)];
    if (!oneWay) return null;
    // Round trip + 20% buffer, rounded up to the nearest ₱50.
    return Math.ceil((oneWay * 2 * 1.2) / 50) * 50;
});

const fuelUrl = computed(() => {
    const toll = result.value ? result.value.rates[String(vehicleClass.value)] : null;
    return toll ? `/tools/fuel-cost-calculator?toll=${encodeURIComponent(toll)}` : '/tools/fuel-cost-calculator';
});

onMounted(async () => {
    try {
        const res = await fetch('/api/calculator/expressways');
        if (!res.ok) throw new Error('load failed');
        expressways.value = await res.json();
    } catch {
        loadError.value = true;
    }
});

function tripMultiple(value) {
    if (value === undefined || value === null) return null;
    return roundTrip.value ? value * 2 : value;
}

function formatRate(value) {
    if (value === undefined || value === null) return '—';
    return new Intl.NumberFormat('en-PH').format(value);
}

/**
 * The user picked a point on a different expressway from the one the other
 * field had pinned. Move to that road rather than refusing: if the other end
 * has a same-named twin there (Alabang is on both SLEX and Skyway) keep it,
 * otherwise clear it so they can choose again on the new road.
 */
function switchRoad(field, point) {
    const otherRef = field === 'entry' ? exitPoint : entryPoint;
    const twin = allPoints.value.find((p) =>
        p.expresswayId === point.expresswayId
        && p.name.toLowerCase() === (otherRef.value?.name ?? '').toLowerCase()
        && p.id !== point.id
    );

    if (field === 'entry') entryPoint.value = point; else exitPoint.value = point;
    otherRef.value = twin ?? null;
    result.value = null;
    error.value = '';

    track('calc_interaction', {
        tool: 'toll',
        action: 'switch_road',
        expressway: point.code,
        kept_other_end: !!twin,
    });

    if (twin && canCalculate.value) calculate();
}

function pickClass(value) {
    vehicleClass.value = value;
    track('calc_interaction', { tool: 'toll', action: 'vehicle_class', vehicle_class: value });
}

function pickTrip(value) {
    roundTrip.value = value;
    track('calc_interaction', { tool: 'toll', action: 'round_trip', round_trip: value });
}

function openDetails() {
    showDetails.value = true;
    resultAction('view_details');
}

function resultAction(action) {
    track('result_action', {
        tool: 'toll',
        action,
        expressway: expresswayCode.value,
        route: result.value ? `${result.value.entry} to ${result.value.exit}` : undefined,
        vehicle_class: vehicleClass.value,
    });
}

function swap() {
    [entryPoint.value, exitPoint.value] = [exitPoint.value, entryPoint.value];
    track('calc_interaction', { tool: 'toll', action: 'swap' });
    if (result.value && canCalculate.value) calculate();
}

function clearAll() {
    entryPoint.value = null;
    exitPoint.value = null;
    vehicleClass.value = 1;
    roundTrip.value = false;
    result.value = null;
    error.value = '';
    track('calc_interaction', { tool: 'toll', action: 'clear' });
}

function summaryText() {
    return `${result.value.expressway} toll, ${result.value.entry} → ${result.value.exit}: ₱${displayedRate.value}${roundTrip.value ? ' round trip' : ''} (Class ${vehicleClass.value})`;
}

async function copySummary() {
    if (!result.value) return;
    try {
        await navigator.clipboard.writeText(`${summaryText()} — ${result.value.route_url || window.location.href}`);
        copied.value = true;
        resultAction('copy');
        setTimeout(() => (copied.value = false), 2000);
    } catch { /* ignore */ }
}

async function shareResult() {
    if (!result.value) return;
    const url = result.value.route_url || window.location.href;
    try {
        if (navigator.share) {
            await navigator.share({ title: 'Toll Fee', text: summaryText(), url });
        } else {
            await navigator.clipboard.writeText(`${summaryText()} — ${url}`);
        }
        shared.value = true;
        resultAction(navigator.share ? 'share' : 'share_copy');
        setTimeout(() => (shared.value = false), 2000);
    } catch {
        /* user cancelled share — ignore */
    }
}

async function calculate() {
    if (!canCalculate.value) return;
    calculating.value = true;
    error.value = '';
    result.value = null;
    try {
        const params = new URLSearchParams({ entry: entryPoint.value.id, exit: exitPoint.value.id });
        const res = await fetch(`/api/calculator/rate?${params}`);
        if (!res.ok) {
            error.value = 'No published rate for this entry/exit pair. Try nearby exits, or check the toll matrix.';
            // The most valuable event on the site: a pair someone wanted and
            // we could not price. This is the content-gap list.
            track('toll_calculate', {
                status: 'no_rate',
                entry: entryPoint.value.name,
                exit: exitPoint.value.name,
                expressway: entryPoint.value.code,
                vehicle_class: vehicleClass.value,
            });
            return;
        }
        result.value = await res.json();
        resultEntry.value = { ...entryPoint.value };
        resultExit.value = { ...exitPoint.value };
        track('toll_calculate', {
            status: 'success',
            entry: result.value.entry,
            exit: result.value.exit,
            expressway: entryPoint.value.code,
            vehicle_class: vehicleClass.value,
            round_trip: roundTrip.value,
            fee: displayedRate.value,
        });
    } catch {
        error.value = 'Something went wrong. Please try again.';
        track('toll_calculate', { status: 'error' });
    } finally {
        calculating.value = false;
    }
}
</script>
